import React, { useEffect, useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ShieldCheckIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
export function PrivacyPolicy() {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [activeSection, setActiveSection] = useState('');
  const sections = [{
    id: 'who-we-are',
    title: t('Quiénes Somos', 'Who We Are')
  }, {
    id: 'info-we-collect',
    title: t('Información que Recopilamos', 'Information We Collect')
  }, {
    id: 'how-we-collect',
    title: t('Cómo Recopilamos Información', 'How We Collect Information')
  }, {
    id: 'how-we-use',
    title: t('Cómo Usamos la Información', 'How We Use Information')
  }, {
    id: 'when-we-share',
    title: t('Cuándo Compartimos Información', 'When We Share Information')
  }, {
    id: 'cookies',
    title: t('Cookies y Tecnologías de Rastreo', 'Cookies and Tracking Technologies')
  }, {
    id: 'data-security',
    title: t('Seguridad de los Datos', 'Data Security')
  }, {
    id: 'data-retention',
    title: t('Retención de Datos', 'Data Retention')
  }, {
    id: 'privacy-choices',
    title: t('Tus Opciones y Derechos de Privacidad', 'Your Privacy Choices and Rights')
  }, {
    id: 'texas-privacy',
    title: t('Aviso de Privacidad de Texas', 'Texas Privacy Notice')
  }, {
    id: 'third-party',
    title: t('Servicios de Terceros', 'Third-Party Services')
  }, {
    id: 'children',
    title: t('Privacidad de los Niños', 'Children’s Privacy')
  }, {
    id: 'contact',
    title: t('Información de Contacto', 'Contact Information')
  }, {
    id: 'changes',
    title: t('Cambios a esta Política', 'Changes to This Privacy Policy')
  }];
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200;
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 150;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-321">
      {/* Header / Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm" data-id="element-322">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-323">
          <div className="flex justify-between items-center h-16" data-id="element-324">
            <Link to="/" className="flex items-center gap-2 text-[#1a365d] hover:text-[#d97706] transition-colors font-medium" data-id="element-325">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-326" />
              {t('Volver al inicio', 'Back to home')}
            </Link>

            <div className="flex items-center bg-gray-100 rounded-lg p-1" data-id="element-327">
              <button onClick={() => setLang('es')} className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${lang === 'es' ? 'bg-[#d97706] text-white shadow-sm' : 'text-gray-600 hover:text-[#1a365d]'}`} data-id="element-328">
                ES
              </button>
              <button onClick={() => setLang('en')} className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${lang === 'en' ? 'bg-[#d97706] text-white shadow-sm' : 'text-gray-600 hover:text-[#1a365d]'}`} data-id="element-329">
                EN
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-20" data-id="element-330">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-331">
          {/* Page Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-12" data-id="element-332">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 text-[#1a365d] mb-6" data-id="element-333">
              <ShieldCheckIcon className="w-8 h-8" data-id="element-334" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1a365d] mb-4" data-id="element-335">
              {t('Política de Privacidad', 'Privacy Policy')}
            </h1>
            <div className="w-24 h-1 bg-[#d97706] mx-auto mb-6" data-id="element-336"></div>
            <p className="text-gray-500" data-id="element-337">
              {t('Fecha de vigencia: 1 de Enero de 2024', 'Effective Date: January 1, 2024')}
            </p>
          </motion.div>

          {/* Sticky Anchor Nav */}
          <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 mb-12 -mx-4 px-4 sm:mx-0 sm:px-0 sm:rounded-xl sm:border sm:shadow-sm overflow-hidden" data-id="element-338">
            <div className="flex overflow-x-auto py-3 px-4 gap-4 scrollbar-hide snap-x" data-id="element-339">
              {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors snap-start ${activeSection === section.id ? 'bg-[#1a365d] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-340">
                  {section.title}
                </button>)}
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 space-y-12" data-id="element-341">
            <section id="who-we-are" className="scroll-mt-32" data-id="element-342">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-343">
                {t('1. Quiénes Somos', '1. Who We Are')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-344">
                {t('Bienvenido a Jesús Silva - Agente de Seguros Profesional ("nosotros", "nuestro" o "la Empresa"). Operamos el sitio web jsilvainsurance.com. Somos una agencia de seguros que atiende a clientes en Texas y otros estados de EE. UU.', 'Welcome to Jesús Silva - Insurance Agent Professional ("we," "our," or "the Company"). We operate the website jsilvainsurance.com. We are an insurance agency serving clients in Texas and may also serve clients in other U.S. states.')}
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-345">
                {t('Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y salvaguardamos tu información cuando visitas nuestro sitio web o interactúas con nuestros servicios. Lee esta política detenidamente. Si no estás de acuerdo con los términos de esta política de privacidad, por favor no accedas al sitio.', 'This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or interact with our services. Please read this policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.')}
              </p>
            </section>

            <section id="info-we-collect" className="scroll-mt-32" data-id="element-346">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-347">
                {t('2. Información que Recopilamos', '2. Information We Collect')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-348">
                {t('Podemos recopilar información personal que nos proporcionas voluntariamente al expresar interés en obtener información sobre nosotros o nuestros productos y servicios. La información personal que recopilamos puede incluir:', 'We may collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and services. The personal information that we collect may include:')}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-349">
                <li data-id="element-350">{t('Nombre completo', 'Full name')}</li>
                <li data-id="element-351">{t('Número de teléfono', 'Phone number')}</li>
                <li data-id="element-352">{t('Dirección de correo electrónico', 'Email address')}</li>
                <li data-id="element-353">
                  {t('Código postal y estado de residencia', 'ZIP code and state of residence')}
                </li>
                <li data-id="element-354">{t('Rango de edad', 'Age range')}</li>
                <li data-id="element-355">
                  {t('Intereses de seguros (ej. salud, vida, Medicare)', 'Insurance interests (e.g., health, life, Medicare)')}
                </li>
                <li data-id="element-356">{t('Preferencias de citas', 'Appointment preferences')}</li>
                <li data-id="element-357">
                  {t('Cualquier otra información que envíes voluntariamente a través de nuestros formularios', 'Any other information you voluntarily submit through our forms')}
                </li>
              </ul>
            </section>

            <section id="how-we-collect" className="scroll-mt-32" data-id="element-358">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-359">
                {t('3. Cómo Recopilamos Información', '3. How We Collect Information')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-360">
                {t('Recopilamos información de las siguientes maneras:', 'We collect information in the following ways:')}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-361">
                <li data-id="element-362">
                  <strong data-id="element-363">
                    {t('Interacciones Directas:', 'Direct Interactions:')}
                  </strong>{' '}
                  {t('Cuando completas formularios de contacto, solicitudes de cotización, formularios de programación de llamadas o te suscribes a boletines informativos.', 'When you fill out contact forms, quote request forms, call scheduling forms, or subscribe to newsletters.')}
                </li>
                <li data-id="element-364">
                  <strong data-id="element-365">
                    {t('Tecnologías Automatizadas:', 'Automated Technologies:')}
                  </strong>{' '}
                  {t('A medida que navegas por nuestro sitio web, podemos recopilar automáticamente cierta información sobre tu equipo, acciones de navegación y patrones utilizando cookies y tecnologías similares.', 'As you navigate through our website, we may automatically collect certain information about your equipment, browsing actions, and patterns using cookies and similar technologies.')}
                </li>
                <li data-id="element-366">
                  <strong data-id="element-367">
                    {t('Herramientas de Análisis y Seguimiento:', 'Analytics and Tracking Tools:')}
                  </strong>{' '}
                  {t('Utilizamos herramientas como Google Analytics, Meta Pixel, seguimiento de formularios y seguimiento de llamadas para comprender cómo los usuarios interactúan con nuestro sitio.', 'We use tools such as Google Analytics, Meta Pixel, form tracking, and call tracking to understand how users interact with our site.')}
                </li>
              </ul>
            </section>

            <section id="how-we-use" className="scroll-mt-32" data-id="element-368">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-369">
                {t('4. Cómo Usamos la Información', '4. How We Use Information')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-370">
                {t('Usamos la información que recopilamos para diversos fines comerciales, que incluyen:', 'We use the information we collect for various business purposes, including:')}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-371">
                <li data-id="element-372">
                  {t('Responder a tus consultas y solicitudes de cotizaciones de seguros.', 'To respond to your inquiries and requests for insurance quotes.')}
                </li>
                <li data-id="element-373">
                  {t('Proporcionar asistencia y servicios relacionados con seguros.', 'To provide insurance-related assistance and services.')}
                </li>
                <li data-id="element-374">
                  {t('Comunicarnos contigo por teléfono, correo electrónico o mensaje de texto con respecto a tu consulta (con tu consentimiento).', 'To communicate with you by phone, email, or text regarding your inquiry (with your consent).')}
                </li>
                <li data-id="element-375">
                  {t('Mejorar nuestro sitio web, servicios y experiencia del usuario.', 'To improve our website, services, and user experience.')}
                </li>
                <li data-id="element-376">
                  {t('Para fines de marketing y publicidad básica, para mostrarte contenido relevante.', 'For marketing and basic advertising purposes, to show you relevant content.')}
                </li>
                <li data-id="element-377">
                  {t('Cumplir con las obligaciones legales aplicables.', 'To comply with applicable legal obligations.')}
                </li>
              </ul>
            </section>

            <section id="when-we-share" className="scroll-mt-32" data-id="element-378">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-379">
                {t('5. Cuándo Compartimos Información', '5. When We Share Information')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-380">
                {t('No vendemos tu información personal a terceros. Podemos compartir tu información en las siguientes situaciones:', 'We do not sell your personal information to third parties. We may share your information in the following situations:')}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-381">
                <li data-id="element-382">
                  <strong data-id="element-383">
                    {t('Proveedores de Seguros:', 'Insurance Carriers:')}
                  </strong>{' '}
                  {t('Para obtener cotizaciones o procesar solicitudes en tu nombre.', 'To obtain quotes or process applications on your behalf.')}
                </li>
                <li data-id="element-384">
                  <strong data-id="element-385">
                    {t('Proveedores de Servicios:', 'Service Providers:')}
                  </strong>{' '}
                  {t('Con terceros que prestan servicios para nosotros, como alojamiento de sitios web, análisis de datos y servicio al cliente.', 'With third parties that perform services for us, such as website hosting, data analysis, and customer service.')}
                </li>
                <li data-id="element-386">
                  <strong data-id="element-387">
                    {t('Obligaciones Legales:', 'Legal Obligations:')}
                  </strong>{' '}
                  {t('Cuando sea requerido por ley, citación o proceso legal, o para proteger nuestros derechos.', 'When required by law, subpoena, or legal process, or to protect our rights.')}
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4 italic text-sm" data-id="element-388">
                {t('Nota: Algunas prácticas de datos pueden estar sujetas a normas federales de privacidad financiera aplicables a seguros y servicios financieros.', 'Note: Some data practices may be subject to federal financial privacy rules applicable to insurance and financial services.')}
              </p>
            </section>

            <section id="cookies" className="scroll-mt-32" data-id="element-389">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-390">
                {t('6. Cookies y Tecnologías de Rastreo', '6. Cookies and Tracking Technologies')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-391">
                {t('Podemos usar cookies, balizas web, píxeles de seguimiento y otras tecnologías de seguimiento en el sitio web para ayudar a personalizar el sitio y mejorar tu experiencia. Al usar el sitio, aceptas nuestro uso de cookies.', 'We may use cookies, web beacons, tracking pixels, and other tracking technologies on the website to help customize the site and improve your experience. By using the site, you agree to our use of cookies.')}
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-392">
                {t('Puedes configurar tu navegador para que rechace todas o algunas de las cookies del navegador, o para que te avise cuando los sitios web configuren o accedan a las cookies. Si desactivas o rechazas las cookies, ten en cuenta que algunas partes de este sitio pueden volverse inaccesibles o no funcionar correctamente.', 'You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of this site may become inaccessible or not function properly.')}
              </p>
            </section>

            <section id="data-security" className="scroll-mt-32" data-id="element-393">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-394">
                {t('7. Seguridad de los Datos', '7. Data Security')}
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-395">
                {t('Hemos implementado medidas de seguridad administrativas, técnicas y físicas razonables diseñadas para proteger tu información personal. Sin embargo, ninguna transmisión electrónica a través de Internet o tecnología de almacenamiento de información puede garantizarse como 100% segura, por lo que no podemos prometer ni garantizar que los piratas informáticos, ciberdelincuentes u otros terceros no autorizados no puedan vencer nuestra seguridad y recopilar, acceder, robar o modificar tu información de manera inapropiada.', 'We have implemented reasonable administrative, technical, and physical security measures designed to protect your personal information. However, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information.')}
              </p>
            </section>

            <section id="data-retention" className="scroll-mt-32" data-id="element-396">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-397">
                {t('8. Retención de Datos', '8. Data Retention')}
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-398">
                {t('Solo conservaremos tu información personal durante el tiempo que sea necesario para los fines establecidos en esta Política de Privacidad, a menos que la ley exija o permita un período de retención más largo (como requisitos fiscales, contables u otros requisitos legales relacionados con la industria de seguros).', 'We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Policy, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements related to the insurance industry).')}
              </p>
            </section>

            <section id="privacy-choices" className="scroll-mt-32" data-id="element-399">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-400">
                {t('9. Tus Opciones y Derechos de Privacidad', '9. Your Privacy Choices and Rights')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-401">
                {t('Dependiendo de dónde residas, puedes tener ciertos derechos con respecto a tu información personal, tales como:', 'Depending on where you reside, you may have certain rights regarding your personal information, such as:')}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-402">
                <li data-id="element-403">
                  {t('El derecho a solicitar acceso a la información personal que tenemos sobre ti.', 'The right to request access to the personal information we hold about you.')}
                </li>
                <li data-id="element-404">
                  {t('El derecho a solicitar que corrijamos o actualicemos tu información personal.', 'The right to request that we correct or update your personal information.')}
                </li>
                <li data-id="element-405">
                  {t('El derecho a solicitar la eliminación de tu información personal.', 'The right to request deletion of your personal information.')}
                </li>
                <li data-id="element-406">
                  {t('El derecho a optar por no recibir comunicaciones de marketing.', 'The right to opt-out of marketing communications.')}
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4" data-id="element-407">
                {t('Para ejercer estos derechos, comunícate con nosotros utilizando la información proporcionada en la sección "Información de Contacto" a continuación.', 'To exercise these rights, please contact us using the information provided in the "Contact Information" section below.')}
              </p>
            </section>

            <section id="texas-privacy" className="scroll-mt-32 bg-blue-50/50 p-6 rounded-xl border border-blue-100" data-id="element-408">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-409">
                {t('10. Aviso de Privacidad de Texas', '10. Texas Privacy Notice')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-410">
                {t('Esta sección se aplica específicamente a los residentes de Texas. De acuerdo con las leyes de privacidad aplicables de Texas, divulgamos lo siguiente:', 'This section applies specifically to residents of Texas. In accordance with applicable Texas privacy laws, we disclose the following:')}
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-4" data-id="element-411">
                <li data-id="element-412">
                  <strong data-id="element-413">
                    {t('Tipos de datos personales recopilados:', 'Types of personal data collected:')}
                  </strong>{' '}
                  {t('Nombre, información de contacto (teléfono, correo electrónico), datos demográficos básicos e información relacionada con seguros proporcionada voluntariamente.', 'Name, contact information (phone, email), basic demographics, and insurance-related information provided voluntarily.')}
                </li>
                <li data-id="element-414">
                  <strong data-id="element-415">
                    {t('Propósitos del procesamiento:', 'Purposes for processing:')}
                  </strong>{' '}
                  {t('Para proporcionar cotizaciones de seguros, responder a consultas, programar citas y comunicarnos sobre servicios de seguros.', 'To provide insurance quotes, respond to inquiries, schedule appointments, and communicate about insurance services.')}
                </li>
                <li data-id="element-416">
                  <strong data-id="element-417">
                    {t('Cómo enviar solicitudes de privacidad:', 'How to submit privacy requests:')}
                  </strong>{' '}
                  {t('Los residentes de Texas pueden enviar solicitudes para acceder, corregir o eliminar sus datos personales contactándonos a jsilvainsurance@gmail.com o llamando al (346) 902-2090.', 'Texas residents may submit requests to access, correct, or delete their personal data by contacting us at jsilvainsurance@gmail.com or calling (346) 902-2090.')}
                </li>
                <li data-id="element-418">
                  <strong data-id="element-419">
                    {t('Proceso de apelación:', 'Appeal process:')}
                  </strong>{' '}
                  {t('Si denegamos tu solicitud de privacidad, puedes apelar nuestra decisión respondiendo a nuestro correo electrónico de denegación con el asunto "Apelación de Privacidad".', 'If we deny your privacy request, you may appeal our decision by replying to our denial email with the subject line "Privacy Appeal."')}
                </li>
              </ul>
            </section>

            <section id="third-party" className="scroll-mt-32" data-id="element-420">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-421">
                {t('11. Servicios de Terceros', '11. Third-Party Services')}
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-422">
                {t('El sitio web puede contener enlaces a sitios web y aplicaciones de interés de terceros, incluidos anuncios y servicios externos, que no están afiliados a nosotros. Una vez que hayas utilizado estos enlaces para salir del sitio web, cualquier información que proporciones a estos terceros no está cubierta por esta Política de Privacidad, y no podemos garantizar la seguridad y privacidad de tu información.', 'The website may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the website, any information you provide to these third parties is not covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your information.')}
              </p>
            </section>

            <section id="children" className="scroll-mt-32" data-id="element-423">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-424">
                {t('12. Privacidad de los Niños', '12. Children’s Privacy')}
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-425">
                {t('No solicitamos información a sabiendas ni comercializamos con niños menores de 13 años. Si nos damos cuenta de que hemos recopilado información personal de un niño menor de 13 años sin la verificación del consentimiento de los padres, eliminaremos esa información lo más rápido posible.', 'We do not knowingly solicit information from or market to children under the age of 13. If we become aware that we have collected personal information from a child under age 13 without verification of parental consent, we will delete that information as quickly as possible.')}
              </p>
            </section>

            <section id="contact" className="scroll-mt-32" data-id="element-426">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-427">
                {t('13. Información de Contacto', '13. Contact Information')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6" data-id="element-428">
                {t('Si tienes preguntas o comentarios sobre esta Política de Privacidad, por favor contáctanos en:', 'If you have questions or comments about this Privacy Policy, please contact us at:')}
              </p>
              <div className="bg-gray-50 rounded-xl p-6 space-y-4" data-id="element-429">
                <p className="font-bold text-[#1a365d] text-lg" data-id="element-430">
                  Jesús Silva - Insurance Agent Professional
                </p>
                <div className="flex items-center gap-3 text-gray-700" data-id="element-431">
                  <PhoneIcon className="w-5 h-5 text-[#d97706]" data-id="element-432" />
                  <a href="tel:3469022090" className="hover:text-[#d97706] transition-colors" data-id="element-433">
                    (346) 902-2090
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-700" data-id="element-434">
                  <MailIcon className="w-5 h-5 text-[#d97706]" data-id="element-435" />
                  <a href="mailto:jsilvainsurance@gmail.com" className="hover:text-[#d97706] transition-colors" data-id="element-436">
                    jsilvainsurance@gmail.com
                  </a>
                </div>
              </div>
            </section>

            <section id="changes" className="scroll-mt-32" data-id="element-437">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-4" data-id="element-438">
                {t('14. Cambios a esta Política', '14. Changes to This Privacy Policy')}
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-439">
                {t('Podemos actualizar esta Política de Privacidad de vez en cuando. La versión actualizada se indicará con una fecha de "Revisión" actualizada y la versión actualizada entrará en vigencia tan pronto como sea accesible. Te recomendamos que revises esta política de privacidad con frecuencia para estar informado de cómo protegemos tu información.', 'We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible. We encourage you to review this privacy policy frequently to be informed of how we are protecting your information.')}
              </p>
            </section>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center" data-id="element-440">
            <p className="text-gray-600 mb-6" data-id="element-441">
              {t('Si tienes preguntas sobre esta Política de Privacidad, por favor contáctanos en jsilvainsurance@gmail.com o (346) 902-2090.', 'If you have questions about this Privacy Policy, please contact us at jsilvainsurance@gmail.com or (346) 902-2090.')}
            </p>
            <Link to="/" className="inline-flex items-center gap-2 bg-[#1a365d] text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-900 transition-colors" data-id="element-442">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-443" />
              {t('Volver al inicio', 'Return to Home')}
            </Link>
          </div>
        </div>
      </main>
    </div>;
}